// Cake Merge (cake-sort) - pure board logic (no DOM).
//
// Each plate holds slices of one or more cake colors. A full cake = CAKE_SIZE
// slices of the SAME color on one plate; a plate's capacity is also CAKE_SIZE.
// When two orthogonally adjacent plates share a color, those slices "jump over"
// and consolidate onto one plate. Filling a plate with a full cake clears it
// (a cake is served); a plate emptied of all slices is removed, freeing its cell.

export const CAKE_SIZE = 6;

/** A plate: counts[color] = number of slices of that color. Total <= CAKE_SIZE. */
export type Plate = number[];
export type Cell = Plate | null;

export interface CakeLevel {
  cols: number;
  rows: number;
  colors: number; // number of active cake colors (difficulty)
  target: number; // cakes to serve to win
  trayMin: number; // min slices on a freshly spawned tray plate
  trayMax: number; // max slices on a freshly spawned tray plate
  boosters: number; // hammer (remove a plate) uses
}

export function emptyBoard(level: CakeLevel): Cell[] {
  return new Array<Cell>(level.cols * level.rows).fill(null);
}

export function plateTotal(p: Plate): number {
  let s = 0;
  for (const v of p) s += v;
  return s;
}

export function cloneCells(cells: Cell[]): Cell[] {
  return cells.map((c) => (c ? c.slice() : null));
}

function neighbors(level: CakeLevel, cell: number): number[] {
  const r = Math.floor(cell / level.cols);
  const c = cell % level.cols;
  const out: number[] = [];
  if (r > 0) out.push(cell - level.cols);
  if (r < level.rows - 1) out.push(cell + level.cols);
  if (c > 0) out.push(cell - 1);
  if (c < level.cols - 1) out.push(cell + 1);
  return out;
}

export interface ResolveResult {
  cakesMade: number;
  changedCells: Set<number>; // cells that changed (for animation)
}

/**
 * Resolve the board to a stable state: consolidate same-color slices between
 * adjacent plates, serve full cakes, and remove empty plates. Mutates `cells`.
 */
export function resolve(level: CakeLevel, cells: Cell[]): ResolveResult {
  const changed = new Set<number>();
  let cakesMade = 0;
  let dirty = true;
  let guard = 0;

  while (dirty && guard++ < 2000) {
    dirty = false;

    // 1. Transfers: for each adjacent pair sharing a color, move slices toward
    //    the plate that already holds more of that color (deterministic ties).
    for (let i = 0; i < cells.length; i++) {
      const a = cells[i];
      if (!a) continue;
      for (const j of neighbors(level, i)) {
        if (j < i) continue; // each unordered pair handled once
        const b = cells[j];
        if (!b) continue;
        for (let c = 0; c < level.colors; c++) {
          if (a[c] === 0 || b[c] === 0) continue;
          // Receiver = more of color c; tie -> lower index (a).
          const aWins = a[c] >= b[c];
          const hi = aWins ? a : b;
          const lo = aWins ? b : a;
          const free = CAKE_SIZE - plateTotal(hi);
          if (free <= 0) continue;
          const move = Math.min(lo[c], free);
          if (move > 0) {
            hi[c] += move;
            lo[c] -= move;
            changed.add(i);
            changed.add(j);
            dirty = true;
          }
        }
      }
    }

    // 2. Serve full cakes.
    for (let i = 0; i < cells.length; i++) {
      const p = cells[i];
      if (!p) continue;
      for (let c = 0; c < level.colors; c++) {
        if (p[c] >= CAKE_SIZE) {
          p[c] = 0;
          cakesMade++;
          changed.add(i);
          dirty = true;
        }
      }
    }

    // 3. Remove empty plates.
    for (let i = 0; i < cells.length; i++) {
      const p = cells[i];
      if (p && plateTotal(p) === 0) {
        cells[i] = null;
        changed.add(i);
        dirty = true;
      }
    }
  }

  return { cakesMade, changedCells: changed };
}

export interface PlaceResult {
  cells: Cell[];
  placed: boolean;
  cakesMade: number;
  changedCells: Set<number>;
}

/** Place a plate on an empty cell and resolve the board. Returns a new cells array. */
export function placePlate(level: CakeLevel, cells0: Cell[], cell: number, plate: Plate): PlaceResult {
  if (cells0[cell] != null) return { cells: cells0, placed: false, cakesMade: 0, changedCells: new Set() };
  const cells = cloneCells(cells0);
  cells[cell] = plate.slice();
  const res = resolve(level, cells);
  res.changedCells.add(cell);
  return { cells, placed: true, cakesMade: res.cakesMade, changedCells: res.changedCells };
}

export function isFull(cells: Cell[]): boolean {
  return cells.every((c) => c != null);
}

export function emptyCount(cells: Cell[]): number {
  return cells.reduce((n, c) => n + (c == null ? 1 : 0), 0);
}

// Deterministic RNG (mulberry32).
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rng: () => number, lo: number, hi: number): number {
  return lo + Math.floor(rng() * (hi - lo + 1));
}

/** Spawn a tray plate: a few slices across 1-2 of the active colors. */
export function spawnPlate(level: CakeLevel, rng: () => number): Plate {
  const plate: Plate = new Array(level.colors).fill(0);
  let total = randInt(rng, level.trayMin, level.trayMax);
  const colorCount = level.colors >= 2 && rng() < 0.4 ? 2 : 1;
  const chosen = new Set<number>();
  while (chosen.size < colorCount) chosen.add(randInt(rng, 0, level.colors - 1));
  const colors = [...chosen];
  for (const c of colors) {
    if (total <= 0) break;
    const give = colors[colors.length - 1] === c ? total : randInt(rng, 1, Math.max(1, total - 1));
    plate[c] += Math.min(give, CAKE_SIZE - 1);
    total -= give;
  }
  // Guarantee at least one slice.
  if (plateTotal(plate) === 0) plate[randInt(rng, 0, level.colors - 1)] = 1;
  return plate;
}
